import React, {memo, SyntheticEvent, useCallback, useEffect, useMemo, useState} from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Grid,
  Box,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  Autocomplete
} from "@mui/material";
import {
  Euro as EuroIcon,
  LocalOffer as LocalOfferIcon,
  PhotoCamera as PhotoCameraIcon,
  NoPhotography as NoPhotographyIcon,
} from "@mui/icons-material";
import {QrScanner} from '@yudiel/react-qr-scanner';

import {importAll} from "../../../Utils";
import {useDebounce, useSupabase} from "../../../Hooks";
import {DISCOUNT_DETAIL_PROPOSITIONS} from "../../../Config";
import {useAppStore} from "../../../Stores";
import {getDefaultDiscountSize, getDefaultPriceSize} from "../Config";
import {FormLabelType} from "../Types";
import {FontSize} from "../../../Components";

interface LabelFormProps {
  id: number;
  isFirst: boolean;
}

export const Form: React.FC<LabelFormProps> = memo(({
  id,
  isFirst
}) => {
  const printOptionsSize = useAppStore((state) => state.printOptions?.size);
  const printOptionsOrientation = useAppStore((state) => state.printOptions?.orientation);

  if (!printOptionsSize || !printOptionsOrientation) {
    throw new Error("Impossible de trouver les options d'impression.");
  }

  const data = useAppStore((state) => state.labels.find((label) => label.id === id) || null);

  if (!data) {
    throw new Error("Impossible de trouver les données de l'étiquette.");
  }

  const addLabel = useAppStore((state) => state.addLabel);
  const updateLabel = useAppStore((state) => state.updateLabel);
  const deleteLabel = useAppStore((state) => state.deleteLabel);

  const scanRef = React.useRef<any>(null);
  const [isAutoPrice, setIsAutoPrice] = useState<boolean>(false);
  const [openScan, setOpenScan] = useState<boolean>(false)
  const [label, setLabel] = useState<FormLabelType>(data as FormLabelType);
  const debouncedValues = useDebounce(label, 500);
  const [productDb, productDbLoading, productDbError] = useSupabase(debouncedValues.ean);

  // Barcode scanner
  const onClickPhoneCamera = useCallback(() => {
    setOpenScan(!openScan);
  }, [openScan]);

  const onScanUpdate = useCallback(
    (result: any) => {
    if (result) {
      setLabel({...label, ean: result});
      setOpenScan(false);
    }
  }, [label]);

  useEffect(() => {
    if (openScan) {
      scanRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [openScan]);

  // Form handles
  const handleAutocompleteChange = useCallback(
    (event: SyntheticEvent<Element, Event>, inputValue: any) => {
      setLabel({
        ...label,
        'discountDetail': inputValue,
      });
    },
    [label]
  );

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setLabel({
        ...label,
        [event.target.name]: event.target.value,
      });
    },
    [label]
  );

  const handleSelectChange = useCallback(
    (event: SelectChangeEvent) => {
      setLabel({
        ...label,
        [event.target.name]: event.target.value,
      });
    },
    [label]
  );

  const toggleIsPriceEuro = useCallback(
    () => {
      setLabel({
        ...label,
        isPriceEuro: !label.isPriceEuro,
      });
    },
    [label]
  );

  // Manageable sizes
  useEffect(() => {
    const discountSize = getDefaultDiscountSize(printOptionsOrientation);
    const priceSize = getDefaultPriceSize(printOptionsOrientation);

    setLabel({...label, discountSize, priceSize});
  }, [printOptionsOrientation]);

  const handleDiscountSize = useCallback(
    (newSize: number) => setLabel({...label, discountSize: newSize}),
    [label]
  );

  const handlePriceSize = useCallback(
    (newSize: number) => setLabel({...label, priceSize: newSize}),
    [label]
  );

  // EAN search
  useEffect(() => {
    if(productDb) {
      setLabel({
        ...label,
        name: productDb.name ?? '',
        capacity: `${productDb.quantity && productDb.quantity > 0 ? productDb.quantity : ''}${productDb.unit ?? ''}`,
      })
    }
  }, [productDb]);

  // Auto price calculation
  useEffect(() => {
    if (label.icon !== 'text' || !label.discount || !label.oldPrice) {
      setIsAutoPrice(false);
      return;
    }

    const discountType = label.discount.includes('€') ? '€' : label.discount.includes('%') ? '%' : null;

    if (!discountType) {
      setIsAutoPrice(false);
      return;
    }

    const discount = parseFloat(label.discount.replace(discountType, '').replace(',', '.'));
    const oldPrice = parseFloat(label.oldPrice.replace(',', '.'));

    const price = discountType === '€' ? oldPrice + discount : oldPrice + (oldPrice * discount / 100);

    setIsAutoPrice(true);

    setLabel({
      ...label,
      price: price.toFixed(2).replace('.', ',').replace(/[.,]00$/, ""),
      isPriceEuro: true,
    });
  }, [label.icon, label.discount, label.oldPrice]);

  // Callback to parent
  useEffect(() => {
    updateLabel(debouncedValues.id, debouncedValues);
  }, [debouncedValues]);

  // Utils variables for rendering
  const icons = importAll(require.context('../../../Icons', false, /\.(svg)$/));
  const topIconsOrder = useMemo(() => ['Euro', 'Lot-de-deux', 'Pouce', 'Pourcentage', 'Top'], []);
  const iconsTop = icons
    .filter((icon: any) => topIconsOrder.includes(icon.split('/').pop().split('.').shift()))
    .sort((a: any, b: any) => {
      return topIconsOrder.indexOf(a.split('/').pop().split('.').shift()) - topIconsOrder.indexOf(b.split('/').pop().split('.').shift());
    });
  iconsTop.forEach((icon: any) => {
    icons.splice(icons.indexOf(icon), 1);
  });
  const helperText = productDbError ? productDbError : productDbLoading ? 'Recherche en cours...' : productDb ? 'Produit trouvé et importé' : ''

  return (
    <>
      {openScan && (
        <div ref={scanRef}>
          <QrScanner onDecode={onScanUpdate} />
        </div>
      )}

      <Box component="form" sx={{mt: 1}}>
        <Grid container spacing={2}>
          <Grid item xs={11}>
            <TextField
              fullWidth
              size="small"
              margin="normal"
              id="ean"
              {...(!!productDb || !!productDbError) && {focused: true}}
              label="Code EAN"
              name="ean"
              value={label.ean}
              onChange={handleInputChange}
              color={productDb ? 'success' : productDbError ? 'error' : 'primary'}
              error={!!productDbError}
              helperText={helperText}
            />
          </Grid>

          <Grid item xs={1}>
            <IconButton aria-label="Scanner avec la caméra" sx={{mt: 2}} onClick={onClickPhoneCamera}>
              {openScan ? <NoPhotographyIcon color="error" /> : <PhotoCameraIcon />}
            </IconButton>
          </Grid>

          {printOptionsOrientation === 'portrait' && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                margin="normal"
                id="title"
                label="Titre"
                name="title"
                value={label.title}
                onChange={handleInputChange}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              margin="normal"
              id="name"
              label="Nom du produit"
              name="name"
              value={label.name}
              onChange={handleInputChange}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              margin="normal"
              id="capacity"
              label="Contenance du produit"
              name="capacity"
              value={label.capacity}
              onChange={handleInputChange}
            />
          </Grid>

          <Grid container item xs={12} spacing={2}>
            <Grid item sm={6}>
              <FormControl fullWidth sx={{mt: 2}}>
                <InputLabel id="icon-label" size="small">
                  Icon
                </InputLabel>
                <Select
                  size="small"
                  labelId="icon-label"
                  id="icon"
                  name="icon"
                  value={label.icon}
                  label="Icon"
                  onChange={handleSelectChange}
                >
                  <MenuItem value="text" key="text">
                    Renseigner un texte
                  </MenuItem>

                  <MenuItem disabled><div style={{borderBottom: "1px solid black", width: "100%", margin: "10px 0"}}></div></MenuItem>

                  {iconsTop && iconsTop.map(function (image: any, index: number) {
                    const fileName = image.split('/').pop().split('.').shift();
                    const componentName = fileName.charAt(0).toUpperCase() + fileName.slice(1);

                    return (
                      <MenuItem value={componentName} key={index}>
                        <div className='iconsItemPreview'>
                          <img alt={fileName} src={image}/>
                        </div>
                        {fileName.replace(/[^a-zA-Z]/g, ' ')}
                      </MenuItem>
                    )
                  })}

                  {iconsTop.length > 0 && <MenuItem disabled><div style={{borderBottom: "1px solid black", width: "100%", margin: "10px 0"}}></div></MenuItem>}

                  {icons.map(function (image: any, index: number) {
                    const fileName = image.split('/').pop().split('.').shift();
                    const componentName = fileName.charAt(0).toUpperCase() + fileName.slice(1);

                    return (
                      <MenuItem value={componentName} key={index}>
                        <div className='iconsItemPreview'>
                          <img alt={fileName} src={image}/>
                        </div>
                        {fileName.replace(/[^a-zA-Z]/g, ' ')}
                      </MenuItem>
                    )
                  })}
                </Select>
              </FormControl>

              <Grid container spacing={2}>
                <Grid item xs={8} sx={{mt: 1}}>
                  <TextField
                    fullWidth
                    size="small"
                    margin="normal"
                    id="discount"
                    label="Promotion"
                    name="discount"
                    multiline={true}
                    value={label.discount}
                    onChange={handleInputChange}
                    disabled={label.icon !== 'text'}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocalOfferIcon fontSize="small"/>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={4} sx={{mt: 3}}>
                  <FontSize
                    fontSize={label.discountSize}
                    onChangeCallback={handleDiscountSize}
                    disabled={label.icon !== 'text'}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item sm={6}>
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <TextField
                    fullWidth
                    size="small"
                    margin="normal"
                    id="price"
                    label="Prix ou Offre"
                    name="price"
                    value={label.price}
                    onChange={handleInputChange}
                    disabled={isAutoPrice}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton aria-label="Valeur en euro" onClick={toggleIsPriceEuro} edge="end" disabled={isAutoPrice}>
                            {label.isPriceEuro && (<EuroIcon fontSize="small" color="primary" />)}
                            {!label.isPriceEuro && (<EuroIcon fontSize="small" />)}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={4} sx={{mt: 2}}>
                  <FontSize
                    fontSize={label.priceSize}
                    onChangeCallback={handlePriceSize}
                  />
                </Grid>
              </Grid>

              <TextField
                fullWidth
                size="small"
                margin="normal"
                id="oldPrice"
                label="Prix barré"
                name="oldPrice"
                type="number"
                value={label.oldPrice}
                onChange={handleInputChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EuroIcon fontSize="small"/>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                freeSolo
                renderInput={(params) => <TextField
                  {...params}
                  fullWidth
                  size="small"
                  margin="normal"
                  id="discountDetail"
                  label="Détail promotion"
                  name="discountDetail"
                />}
                options={DISCOUNT_DETAIL_PROPOSITIONS}
                onInputChange={handleAutocompleteChange}
                inputValue={label.discountDetail}
              />
            </Grid>
          </Grid>

          <Grid item xs={6}>
            {isFirst ? null : (
              <Button
                fullWidth
                variant="contained"
                sx={{mt: 3, mb: 2}}
                onClick={() => deleteLabel(label.id)}
                color={"error"}
              >
                Supprimer l'étiquette
              </Button>
            )}
          </Grid>

          <Grid item xs={6}>
            <Button
              fullWidth
              variant="contained"
              sx={{mt: 3, mb: 2}}
              onClick={() => addLabel({})}
              color={"primary"}
            >
              Ajouter une étiquette
            </Button>
          </Grid>
        </Grid>
      </Box>
    </>
  );
});

export default Form;
